import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api from "@/lib/api";
import { Plus, X } from "lucide-react";
import FileUploader from "@/components/FileUploader";
import { formatApiError } from "@/lib/utils2";
import { toast } from "sonner";

const COMMON_TASKS = [
  "Oil change",
  "Air filter replacement",
  "Fuel filter replacement",
  "Tire rotation",
  "Tire replacement",
  "Brake inspection",
  "Brake pads",
  "Coolant flush",
  "Spark plug replacement",
  "Battery replacement",
  "Belt replacement",
  "Greasing",
  "Blade sharpen/replace",
  "Chain lubrication",
  "Annual inspection",
];

export default function AddMaintenanceDialog({ equipmentId, onClose, onSaved, defaultTask }) {
  const [projects, setProjects] = useState([]);
  const [equipment, setEquipment] = useState(null);
  const [form, setForm] = useState({
    equipment_id: equipmentId,
    task: defaultTask || "",
    performed_at: new Date().toISOString().slice(0, 10),
    hours: "",
    cost: "",
    notes: "",
    parts_used: "",
    links: [],
    photo_ids: [],
    project_id: "",
  });
  const [newLink, setNewLink] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/projects").then(({ data }) => setProjects(data.items)).catch(() => {});
    api.get(`/equipment/${equipmentId}`).then(({ data }) => setEquipment(data)).catch(() => {});
  }, [equipmentId]);

  const usageUnit = equipment?.usage_unit || "hours";
  const usageLabel = usageUnit === "miles" ? "Mileage" : usageUnit === "hours" ? "Hours" : null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.task.trim()) {
      toast.error("Task is required");
      return;
    }
    setSaving(true);
    try {
      const body = {
        ...form,
        hours: form.hours ? parseFloat(form.hours) : null,
        cost: form.cost ? parseFloat(form.cost) : null,
        performed_at: new Date(form.performed_at).toISOString(),
        project_id: form.project_id || null,
      };
      await api.post("/maintenance", body);
      toast.success("Maintenance logged");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const addLink = () => {
    if (!newLink.trim()) return;
    setForm({ ...form, links: [...form.links, newLink.trim()] });
    setNewLink("");
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-2xl max-h-[90vh] overflow-y-auto rounded-sm">
        <DialogHeader>
          <div className="text-[10px] uppercase tracking-[0.3em] text-orange-500 mb-1">// New log entry</div>
          <DialogTitle className="font-display font-black text-2xl tracking-tight">Log maintenance</DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Task</Label>
            <div className="flex gap-2 mt-1">
              <Input
                list="common-tasks"
                required
                value={form.task}
                onChange={(e) => setForm({ ...form, task: e.target.value })}
                placeholder="e.g. Oil change"
                className="bg-zinc-950 border-zinc-800 focus:border-orange-500 rounded-sm"
                data-testid="maintenance-task"
              />
              <datalist id="common-tasks">
                {COMMON_TASKS.map((t) => <option key={t} value={t} />)}
              </datalist>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Date</Label>
              <Input
                type="date"
                value={form.performed_at}
                onChange={(e) => setForm({ ...form, performed_at: e.target.value })}
                className="mt-1 bg-zinc-950 border-zinc-800 focus:border-orange-500 rounded-sm"
                data-testid="maintenance-date"
              />
            </div>
            {usageLabel ? (
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{usageLabel}</Label>
                <Input
                  type="number"
                  step={usageUnit === "miles" ? "1" : "0.1"}
                  value={form.hours}
                  onChange={(e) => setForm({ ...form, hours: e.target.value })}
                  className="mt-1 bg-zinc-950 border-zinc-800 focus:border-orange-500 rounded-sm font-mono"
                  data-testid="maintenance-hours"
                  placeholder={usageUnit === "miles" ? "45,000" : "325"}
                />
              </div>
            ) : null}
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Cost ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={form.cost}
                onChange={(e) => setForm({ ...form, cost: e.target.value })}
                className="mt-1 bg-zinc-950 border-zinc-800 focus:border-orange-500 rounded-sm font-mono"
                data-testid="maintenance-cost"
              />
            </div>
          </div>

          {projects.length > 0 && (
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Project (optional)</Label>
              <Select value={form.project_id || "none"} onValueChange={(v) => setForm({ ...form, project_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="none">None</SelectItem>
                  {projects.map((p) => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="mt-1 bg-zinc-950 border-zinc-800 focus:border-orange-500 rounded-sm"
              rows={3}
              placeholder="What did you do, how you did it, tricks to remember..."
              data-testid="maintenance-notes"
            />
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Parts used</Label>
            <Input
              value={form.parts_used}
              onChange={(e) => setForm({ ...form, parts_used: e.target.value })}
              className="mt-1 bg-zinc-950 border-zinc-800 focus:border-orange-500 rounded-sm"
              placeholder="Brand, SKU, quantity..."
              data-testid="maintenance-parts"
            />
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Links (product / how-to)</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={newLink}
                onChange={(e) => setNewLink(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLink(); } }}
                placeholder="https://youtube.com/..."
                className="bg-zinc-950 border-zinc-800 focus:border-orange-500 rounded-sm"
              />
              <Button type="button" onClick={addLink} variant="secondary" className="bg-zinc-800 hover:bg-zinc-700 rounded-sm">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {form.links.length > 0 && (
              <ul className="mt-2 space-y-1">
                {form.links.map((l, i) => (
                  <li key={`${l}-${i}`} className="flex items-center gap-2 text-xs bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1.5">
                    <span className="flex-1 truncate text-orange-400">{l}</span>
                    <button type="button" onClick={() => setForm({ ...form, links: form.links.filter((_, j) => j !== i) })} className="text-zinc-500 hover:text-red-400">
                      <X className="w-3 h-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2 block">Photos / videos</Label>
            <FileUploader
              value={form.photo_ids}
              onChange={(ids) => setForm({ ...form, photo_ids: ids })}
              accept="image/*,video/*"
              max={10}
              label="Media"
            />
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={onClose} className="text-zinc-400">Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-orange-600 hover:bg-orange-500 rounded-sm uppercase tracking-widest text-xs font-bold" data-testid="save-maintenance">
              {saving ? "Saving..." : "Save entry"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
